/**
 * Constants for panel source selection and branch hierarchy
 * Shared across ModalCreateOrderContainer, BranchHierarchyTree, and related modules
 */

export const REGGORA_PANEL_ID = 'reggoraPanel'

export const PANEL_SOURCE = {
  BRANCH: 'branch',
  OUR_PANEL: 'ourPanel',
  PREFERRED: 'preferred',
  REGGORA_PANEL: 'reggoraPanel',
}

export const REGGORA_NETWORK_NAME = 'Reggora Network'

export const LENDER_PANEL_KEY_PREFIX = 'lender-panel-'

/**
 * Hierarchy types based on backend data format:
 * - BRANCH: Non-AMP users, uses branch_id field, aggregates with branch_ids
 * - PREFERRED_PANEL: AMP users, uses id field, aggregates with preferred_panel_ids
 */
export const HIERARCHY_TYPE = {
  BRANCH: 'branch',
  PREFERRED_PANEL: 'preferred_panel',
}

/**
 * Tooltip messages for panel source hierarchy
 * Provides context-aware messaging based on hierarchy type
 */
export const PANEL_SOURCE_TOOLTIPS = {
  [HIERARCHY_TYPE.BRANCH]: {
    panelistCount: 'The numbers shown next to each branch indicate the amount of eligible panelists in that branch.',
    disabledItem: 'This branch does not have any eligible panelists for this order',
  },
  [HIERARCHY_TYPE.PREFERRED_PANEL]: {
    panelistCount: 'The numbers shown next to each preferred panel indicate the amount of eligible panelists in that preferred panel.',
    disabledItem: 'This panel does not have any eligible panelists for this order',
  },
}

/**
 * Gets the appropriate tooltip message based on hierarchy type
 * @param {string} hierarchyType - HIERARCHY_TYPE.BRANCH or HIERARCHY_TYPE.PREFERRED_PANEL
 * @param {string} tooltipKey - 'panelistCount' or 'disabledItem'
 * @returns {string} The tooltip message
 */
export const getPanelSourceTooltip = (hierarchyType, tooltipKey) => {
  const type = hierarchyType || HIERARCHY_TYPE.BRANCH
  return PANEL_SOURCE_TOOLTIPS[type]?.[tooltipKey] || PANEL_SOURCE_TOOLTIPS[HIERARCHY_TYPE.BRANCH][tooltipKey]
}

/**
 * Backend selection_source values for the `requested` array on order payloads.
 * Maps where a panelist/vendor was selected from in the UI.
 */
export const BACKEND_SELECTION_SOURCE = {
  BRANCH: 'branch',
  LENDER_PANEL: 'lender_panel',
  PREFERRED_PANEL: 'preferred_panel',
  REGGORA_NETWORK: 'reggora_network',
}

/**
 * Derives the backend selection_source and optional source_branch_id / source_panel_id
 * based on the current UI selection state.
 *
 * @param {string|null} selectedBranchId - The selected branch/panel ID from selectedFilters
 * @param {boolean} is_amc_lender - Whether the current user is an AMC (AMP) lender
 * @returns {{ selection_source: string, source_branch_id?: string, source_panel_id?: string }}
 */
export const getSelectionSourceInfo = (selectedBranchId, is_amc_lender) => {
  if (selectedBranchId === REGGORA_PANEL_ID) {
    return { selection_source: BACKEND_SELECTION_SOURCE.REGGORA_NETWORK }
  }
  if (!selectedBranchId) {
    return { selection_source: BACKEND_SELECTION_SOURCE.LENDER_PANEL }
  }
  if (is_amc_lender) {
    return { selection_source: BACKEND_SELECTION_SOURCE.PREFERRED_PANEL, source_panel_id: selectedBranchId }
  }
  return { selection_source: BACKEND_SELECTION_SOURCE.BRANCH, source_branch_id: selectedBranchId }
}
